import {proxy} from "valtio";

import {BaseBlogsModelClass} from "@/features/blogs/base-blogs.model";

import {Field, type FieldInstance} from "@/shared/models/form/fields";
import {type InfoCardProps} from "@/shared/ui/data-display/cards/info-card";
import {Tag} from "@/shared/ui/data-display/tags/tag-base";

type PopularPostsConfig = {
    tags: ReadonlyArray<Tag>;
    posts: ReadonlyArray<InfoCardProps>;
    pageSize?: number;
    initialTag?: string;
};

class PopularPostsClass extends BaseBlogsModelClass {
    // Config
    readonly tags: ReadonlyArray<Tag>;

    // Reactive state
    activeTag?: string;
    visibleCount: number;
    search: FieldInstance;

    constructor(config: PopularPostsConfig) {
        super(config.pageSize ?? 8);
        this.tags = config.tags;
        this.posts = config.posts as Array<InfoCardProps>;
        this.activeTag = config.initialTag;
        this.visibleCount = this.pageSize;
        this.search = Field({initialValue: ""});

        // Reset visibleCount when search changes
        this.search.events.on("change", () => {
            this.visibleCount = this.pageSize;
        });
    }

    init(): void {
        /* empty */
    }

    // Computed
    get filteredPosts(): Array<InfoCardProps> {
        if (!this.activeTag || this.activeTag === "All") return this.posts;
        return this.posts.filter((p) => p.category?.title === this.activeTag);
    }

    get searchedPosts(): Array<InfoCardProps> {
        const query = this.search.value.trim().toLowerCase();
        if (!query) return this.filteredPosts;
        return this.filteredPosts.filter((p) => {
            const inTitle = p.title?.toLowerCase().includes(query);
            const description = (p.excerpt || p.description || "").toLowerCase();
            return inTitle || description.includes(query);
        });
    }

    get visiblePosts(): Array<InfoCardProps> {
        return this.searchedPosts.slice(0, this.visibleCount);
    }

    override get hasMore(): boolean {
        return this.visibleCount < this.searchedPosts.length;
    }

    // Actions
    setActiveTag(tag: string): void {
        this.activeTag = tag;
        this.visibleCount = this.pageSize;
    }

    loadMore(): void {
        this.visibleCount = Math.min(this.visibleCount + this.pageSize, this.searchedPosts.length);
    }
}

export type PopularPostsInstance = PopularPostsClass;

export function PopularPostsModel(config: PopularPostsConfig): PopularPostsInstance {
    return proxy<PopularPostsInstance>(new PopularPostsClass(config));
}
